import React, { useEffect, useRef, useState } from 'react';
import HOCWithDisplayName from '../HOCWithDisplayName';

const {
  __slab: { CSP_NONCE },
} = window;
const urlLoaded: Record<string, boolean> = {};

type Options = { dataset?: DOMStringMap };

const useScript = (url: string, { dataset }: Options = {}) => {
  const scriptRef = useRef<HTMLScriptElement>(undefined);
  const [hasLoaded, setHasLoaded] = useState(urlLoaded[url]);

  useEffect(() => {
    if (hasLoaded) {
      return undefined;
    }
    const script = document.createElement('script');
    script.src = url;
    script.nonce = CSP_NONCE;
    script.async = true;
    Object.assign(script.dataset, dataset || {});

    script.onload = () => {
      urlLoaded[url] = true;
      setHasLoaded(true);
    };

    scriptRef.current = script;

    document.body.appendChild(script);

    return () => {
      if (scriptRef.current) {
        scriptRef.current.onload = null;
      }
    };
  }, [url, dataset, hasLoaded]);

  return { hasLoaded };
};

export const withScript =
  (url: string, options?: Options) =>
  <P extends object>(Component: React.ComponentType<P>) => {
    const WrappedComponent = (props: P) => {
      const { hasLoaded } = useScript(url, options);
      return hasLoaded ? <Component {...props} /> : null;
    };

    return HOCWithDisplayName('withScript')(WrappedComponent);
  };

export default useScript;
